import { isApple } from 'utils/general';

const MANUAL_OPEN_BANNER_ID = 'rh-digital-download-file-banner';
const OBJECT_URL_REVOKE_DELAY_MS = 60_000;

export type DownloadFileResult = 'shared' | 'downloaded' | 'opened' | 'manual';

export type DownloadFileFromBase64Params = {
  encodedFile: string;
  fileName: string;
  mimeType?: string;
};

// Extrai o base64 puro de data URI ou string base64
export function extractBase64Payload(encodedFile: string): string {
  if (encodedFile.includes(',')) {
    return encodedFile.split(',')[1] ?? '';
  }

  return encodedFile;
}

// Converte base64 (data URI ou puro) em Blob
export function base64ToBlob(encodedFile: string, mimeType: string): Blob {
  const base64Payload = extractBase64Payload(encodedFile);

  if (!base64Payload) {
    throw new Error('Arquivo base64 inválido ou vazio');
  }

  const binaryString = atob(base64Payload);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
}

function revokeObjectUrlLater(objectUrl: string) {
  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, OBJECT_URL_REVOKE_DELAY_MS);
}

function removeManualOpenBanner() {
  const existingBanner = document.getElementById(MANUAL_OPEN_BANNER_ID);
  existingBanner?.remove();
}

// Exibe banner com ação manual quando o popup é bloqueado após a chamada async
function showManualOpenBanner(objectUrl: string, fileName: string) {
  removeManualOpenBanner();

  const banner = document.createElement('div');
  banner.id = MANUAL_OPEN_BANNER_ID;
  banner.setAttribute('role', 'alertdialog');
  banner.setAttribute('aria-label', 'Abrir arquivo');
  banner.style.cssText = [
    'position:fixed',
    'left:12px',
    'right:12px',
    'bottom:12px',
    'z-index:99999',
    'display:flex',
    'flex-direction:column',
    'gap:12px',
    'padding:16px',
    'border-radius:8px',
    'background:#004186',
    'color:#FFFFFF',
    'font-family:Arial,sans-serif',
    'box-shadow:0 4px 16px rgba(0,0,0,0.25)',
  ].join(';');

  const message = document.createElement('p');
  message.textContent =
    'Não foi possível abrir o arquivo automaticamente. Toque no botão para visualizar ou salvar.';
  message.style.cssText = 'margin:0;font-size:14px;line-height:1.4';

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap';

  const openButton = document.createElement('button');
  openButton.type = 'button';
  openButton.textContent = 'Abrir arquivo';
  openButton.style.cssText = [
    'border:none',
    'border-radius:4px',
    'padding:10px 14px',
    'background:#FFFFFF',
    'color:#004186',
    'font-size:14px',
    'font-weight:700',
    'cursor:pointer',
  ].join(';');

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = 'Fechar';
  closeButton.style.cssText = [
    'border:1px solid #FFFFFF',
    'border-radius:4px',
    'padding:10px 14px',
    'background:transparent',
    'color:#FFFFFF',
    'font-size:14px',
    'cursor:pointer',
  ].join(';');

  const cleanup = () => {
    removeManualOpenBanner();
    URL.revokeObjectURL(objectUrl);
  };

  openButton.addEventListener('click', () => {
    const openedWindow = window.open(objectUrl, '_blank');

    if (!openedWindow) {
      window.location.assign(objectUrl);
    }

    cleanup();
  });

  closeButton.addEventListener('click', cleanup);

  actions.appendChild(closeButton);
  actions.appendChild(openButton);
  banner.appendChild(message);
  banner.appendChild(actions);
  document.body.appendChild(banner);

  // Garante limpeza do object URL mesmo se o usuário ignorar o banner
  window.setTimeout(() => {
    const stillVisible = document.getElementById(MANUAL_OPEN_BANNER_ID);
    if (stillVisible) {
      cleanup();
    }
  }, OBJECT_URL_REVOKE_DELAY_MS);

  // Mantém fileName acessível para inspeção/debug sem logar conteúdo do arquivo
  banner.dataset.fileName = fileName;
}

function triggerAnchorDownload(objectUrl: string, fileName: string) {
  const downloadLink = document.createElement('a');
  downloadLink.href = objectUrl;
  downloadLink.download = fileName;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

function canShareFiles(file: File): boolean {
  /* eslint-disable no-unused-vars */
  const { canShare } = navigator as Navigator & {
    canShare?: (shareData: { files: File[] }) => boolean;
  };
  /* eslint-enable no-unused-vars */

  if (typeof canShare !== 'function') {
    return false;
  }

  try {
    return canShare({ files: [file] });
  } catch {
    return false;
  }
}

// Faz download/abertura de arquivo base64 com fallbacks para iOS Safari e demais browsers
export async function downloadFileFromBase64({
  encodedFile,
  fileName,
  mimeType = 'application/pdf',
}: DownloadFileFromBase64Params): Promise<DownloadFileResult> {
  const blob = base64ToBlob(encodedFile, mimeType);
  const file = new File([blob], fileName, { type: mimeType });

  // 1. Web Share: melhor experiência no iOS quando o gesto do toque ainda está ativo
  if (canShareFiles(file) && typeof navigator.share === 'function') {
    try {
      await navigator.share({
        files: [file],
        title: fileName,
      });
      return 'shared';
    } catch (error) {
      const shareError = error as { name?: string };

      // Usuário cancelou o share sheet — não é falha de download
      if (shareError?.name === 'AbortError') {
        return 'shared';
      }
      // Demais erros (ex.: gesto expirado após a chamada da API) caem para a âncora
    }
  }

  const objectUrl = URL.createObjectURL(blob);

  // 2. Âncora com blob URL: abre/baixa direto no iOS, Android e desktop (sem popup)
  try {
    triggerAnchorDownload(objectUrl, fileName);
    revokeObjectUrlLater(objectUrl);
    return isApple() ? 'opened' : 'downloaded';
  } catch {
    // 3. Último recurso: banner com ação manual dentro do gesto do usuário
    showManualOpenBanner(objectUrl, fileName);
    return 'manual';
  }
}
